/**
 * Validation utilities for filter inputs
 */

interface FilterDefinition {
  label: string;
  type: 'string' | 'number' | 'date';
  operators: string[];
}

// Define field types for validation
const FILTER_FIELD_TYPES: Record<string, FilterDefinition['type']> = {
  title: 'string',
  'genres.name': 'string',
  runtime: 'number',
  release_date: 'date',
  release_year: 'number',
  average_rating: 'number',
  user_average_rating: 'number',
  my_rating: 'number',
  _ratedBy: 'string',
  _watchlistedBy: 'string',
};

export interface ValidationError {
  isValid: boolean;
  message?: string;
}

/**
 * Validate a filter value based on the field type and operator
 */
export function validateFilterValue(
  fieldId: string,
  operator: string,
  value: string,
): ValidationError {
  if (!value || value.trim() === '') {
    return { isValid: false, message: 'Value is required' };
  }

  const fieldType = FILTER_FIELD_TYPES[fieldId] || 'string';

  switch (fieldType) {
    case 'number':
      return validateNumberValue(value, fieldId);
    case 'date':
      return validateDateValue(value);
    case 'string':
      return validateStringValue(value);
    default:
      return { isValid: true };
  }
}

function validateNumberValue(value: string, fieldId: string): ValidationError {
  const num = parseFloat(value);

  if (isNaN(num)) {
    return { isValid: false, message: `Please enter a valid number` };
  }

  // Rating fields: 0-10
  if (fieldId.includes('rating')) {
    if (num < 0 || num > 10) {
      return {
        isValid: false,
        message: 'Rating must be between 0 and 10',
      };
    }
  }

  // Runtime: >= 0
  if (fieldId === 'runtime') {
    if (num < 0) {
      return { isValid: false, message: 'Runtime must be positive' };
    }
  }

  return { isValid: true };
}

function validateDateValue(value: string): ValidationError {
  // Accept formats: YYYY-MM-DD
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

  if (!dateRegex.test(value)) {
    return {
      isValid: false,
      message: 'Date format must be YYYY-MM-DD',
    };
  }

  // Validate that it's an actual date
  const date = new Date(value + 'T00:00:00Z');
  if (isNaN(date.getTime())) {
    return { isValid: false, message: 'Invalid date' };
  }

  return { isValid: true };
}

function validateStringValue(value: string): ValidationError {
  // Minimum 1 character (already checked for empty above)
  if (value.length > 500) {
    return {
      isValid: false,
      message: 'Value is too long (max 500 characters)',
    };
  }

  return { isValid: true };
}

/**
 * Get the display name for a field type
 */
export function getFieldType(fieldId: string): string {
  const type = FILTER_FIELD_TYPES[fieldId] || 'string';
  const typeLabels: Record<string, string> = {
    string: 'text',
    number: 'number',
    date: 'date (YYYY-MM-DD)',
  };
  return typeLabels[type] || type;
}
